import React, { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { schedule } from "utils/scheduleThunk";
import toast from "react-hot-toast";
import { getQuiz } from "utils/quizThunk";

const validationSchema = Yup.object({
  quizId: Yup.string().required("Quiz selection is required"),
  title: Yup.string().required("Exam title is required"),
  date: Yup.date()
    .required("Exam date is required"),
    // .min(new Date(), "Exam Date cannot be in the past"),
  resultDate: Yup.date()
    .required("Exam date is required")
    .min(new Date(), "Result Date cannot be in the past"),
  time: Yup.string().required("Exam time is required"),
  questions: Yup.number()
    .required("No of Questions is required")
    .min(1, "No of Questions  must be at least 1 ")
    .integer("No of Questions  must be an integer"),
  duration: Yup.number()
    .required("Duration is required")
    .min(1, "Duration must be at least 1 minute")
    .integer("Duration must be an integer"),
});

const EditExamModal = ({ exam, closeModal, onExamChange }) => {
  const dispatch = useDispatch();
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  console.log(exam);
  // Fetch quizzes on mount
  useEffect(() => {
    const fetchQuizzes = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await dispatch(getQuiz({})); // Adjust if getQuiz thunk is different
        console.log(getQuiz);
        if (getQuiz.fulfilled.match(result)) {
          console.log("Fetched quizzes:", result.payload.quizzes);
          setQuizzes(result.payload.quizzes || []);
        } else {
          throw new Error(result.payload?.message || "Failed to fetch quizzes");
        }
      } catch (err) {
        setError(err.message);
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchQuizzes();
  }, [dispatch]);

  const initialValues = {
    quizId: exam?.quizId?._id || "",
    title: exam?.description || "",
    date: exam?.examDate || "",
    time: exam?.examTime || "",
    questions: exam?.questions || "",
    duration: exam?.duration || "",
    resultDate: exam?.resultDate || "",
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      console.log("Enter key prevented from submitting form");
    }
  };

  const handleSubmit = async (values, { resetForm }) => {
    setLoading(true);
    setError(null);
    try {
      const payload = {
        scheduleExamId: exam?._id, // For update
        quizId: values.quizId,
        examDate: values.date,
        resultDate: values.resultDate,
        examTime: values.time,
        description: values.title,
        questions: Number(values.questions),
        duration: Number(values.duration),
        isSchedule: true,
        status: exam?.status || "not-completed", // Preserve existing status or default
      };
      console.log("Submitting exam update with payload:", payload);

      const result = await dispatch(schedule(payload));
      if (schedule.fulfilled.match(result)) {
        toast.success("Exam updated successfully!");
        resetForm();
        closeModal();
        if (onExamChange) onExamChange(); // Trigger refresh in parent
      } else {
        throw new Error(result.payload?.message || "Failed to update exam");
      }
    } catch (err) {
      setError(err.message);
      toast.error(err.message || "An error occurred while updating the exam");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" id="edit_exam">
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="fw-bold">Edit Exam</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              onClick={closeModal}
              aria-label="Close modal"
            />
          </div>
          <div className="modal-body">
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize
              validateOnChange
              validateOnBlur
            >
              {({ values, touched, errors }) => (
                <Form
                  id="edit-exam-form"
                  className="row"
                  onKeyDown={handleKeyDown}
                >
                  <div className="mb-3 col-12">
                    <label className="form-label" htmlFor="quizId">
                      Select Quiz
                    </label>
                    <Field
                      as="select"
                      name="quizId"
                      id="quizId"
                      className="form-control"
                      aria-label="Select quiz"
                    >
                      <option value="">Select a Quiz</option>
                      {quizzes.map((quiz) => (
                        <option key={quiz._id} value={quiz._id}>
                          {quiz.quizTitle}
                        </option>
                      ))}
                    </Field>
                    {loading && <div>Loading quizzes...</div>}
                    {error && <div className="text-danger">{error}</div>}
                    {touched.quizId && errors.quizId && (
                      <div className="text-danger">{errors.quizId}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="title">
                      Exam Title
                    </label>
                    <Field
                      type="text"
                      name="title"
                      id="title"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam title"
                    />
                    {touched.title && errors.title && (
                      <div className="text-danger">{errors.title}</div>
                    )}
                  </div>
                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="duration">
                      No of Questions
                    </label>
                    <Field
                      type="number"
                      name="questions"
                      id="questions"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="No of Questions"
                    />
                    {touched.questions && errors.questions && (
                      <div className="text-danger">{errors.questions}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="date">
                      Exam Date
                    </label>
                    <Field
                      type="date"
                      name="date"
                      id="date"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam date"
                    />
                    {touched.date && errors.date && (
                      <div className="text-danger">{errors.date}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="time">
                      Exam Start Time
                    </label>
                    <Field
                      type="time"
                      name="time"
                      id="time"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam time"
                    />
                    {touched.time && errors.time && (
                      <div className="text-danger">{errors.time}</div>
                    )}
                  </div>

                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="duration">
                      Exam Duration (in minutes)
                    </label>
                    <Field
                      type="number"
                      name="duration"
                      id="duration"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Exam duration"
                    />
                    {touched.duration && errors.duration && (
                      <div className="text-danger">{errors.duration}</div>
                    )}
                  </div>
                  <div className="mb-3 col-sm-6">
                    <label className="form-label" htmlFor="date">
                      Result Announcement Date
                    </label>
                    <Field
                      type="date"
                      name="resultDate"
                      id="resultDate"
                      className="form-control"
                      onKeyDown={handleKeyDown}
                      aria-label="Result date"
                    />
                    {touched.resultDate && errors.resultDate && (
                      <div className="text-danger">{errors.resultDate}</div>
                    )}
                  </div>

                  <div className="modal-footer mt-3">
                    <button
                      type="button"
                      className="btn bg-gray-100 rounded-pill mx-2"
                      data-bs-dismiss="modal"
                      onClick={closeModal}
                      aria-label="Cancel"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-secondary rounded-pill"
                      disabled={Object.keys(errors).length > 0 || loading}
                      aria-label="Save changes"
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditExamModal;
